import { useEffect, useMemo, useState } from 'react';

import { extractApiMessage } from '../../api/client';
import {
  useOutreachSenders,
  useTestSendEvent,
} from '../../hooks/useOutreach';
import type { Campaign } from '../../types/outreach';
import { useAuthUser } from '../../auth/useAuthUser';
import styles from '../pages/OutreachPage.module.css';

const INTERNAL_DOMAIN_SUFFIX = '@ember-infra.com';

/**
 * Modal: send a real Microsoft Graph calendar invite using the campaign's
 * Event Details as-is, routed to an internal address for preview. The
 * recipient gets a calendar invite (subject prefixed "[TEST]") they can
 * delete after reviewing.
 *
 * Mirrors TestSendModal's 3-mode picker (me / sender / custom) so the
 * UX is consistent across the two test-send affordances. No queue row,
 * no Affinity write-back, no recipient state change.
 */
export function EventTestSendModal({
  campaign,
  onClose,
}: {
  campaign: Campaign;
  onClose: () => void;
}) {
  const sendersQ = useOutreachSenders();
  const sendMut = useTestSendEvent();
  const auth = useAuthUser();

  const defaultEmail =
    auth.email && auth.email.toLowerCase().endsWith(INTERNAL_DOMAIN_SUFFIX)
      ? auth.email
      : '';

  const [mode, setMode] = useState<'me' | 'sender' | 'custom'>(
    defaultEmail ? 'me' : 'sender',
  );
  const [customEmail, setCustomEmail] = useState('');
  const [pickedSenderEmail, setPickedSenderEmail] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (sendersQ.data && !pickedSenderEmail && sendersQ.data.length > 0) {
      setPickedSenderEmail(sendersQ.data[0].email);
    }
  }, [sendersQ.data, pickedSenderEmail]);

  const internalSenders = useMemo(
    () =>
      (sendersQ.data || []).filter(
        (s) => s.email.toLowerCase().endsWith(INTERNAL_DOMAIN_SUFFIX),
      ),
    [sendersQ.data],
  );

  const eventConfigured =
    campaign.event_starts_at != null && campaign.event_ends_at != null;

  function resolveTestEmail(): string {
    if (mode === 'me') return defaultEmail;
    if (mode === 'sender') return pickedSenderEmail;
    return customEmail.trim();
  }

  async function handleSend() {
    setError(null);
    const email = resolveTestEmail().toLowerCase();
    if (!email) {
      setError('Pick a destination email.');
      return;
    }
    if (!email.endsWith(INTERNAL_DOMAIN_SUFFIX)) {
      setError(`Test recipient must be an internal address (${INTERNAL_DOMAIN_SUFFIX}).`);
      return;
    }
    try {
      const r = await sendMut.mutateAsync({
        campaignId: campaign.id,
        testEmail: email,
      });
      alert(r.message || `Test calendar invite sent to ${r.sent_to}.`);
      onClose();
    } catch (e: unknown) {
      setError(extractApiMessage(e));
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFFFFF', padding: 20, borderRadius: 8,
          minWidth: 460, maxWidth: 560,
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 600 }}>
          Send test calendar invite
        </h3>
        <div style={{ fontSize: 12, color: '#666', marginBottom: 16 }}>
          A real Microsoft Graph calendar invite for{' '}
          <strong>{campaign.name}</strong> lands in the chosen mailbox's
          calendar. Subject prefixed <code>[TEST]</code> so it's easy
          to spot and delete after preview.
        </div>

        {!eventConfigured && (
          <div className={styles.error}>
            This campaign has no event date/time configured yet. Set
            Event Details on the Overview tab first.
          </div>
        )}
        {error && <div className={styles.error}>{error}</div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              cursor: defaultEmail ? 'pointer' : 'not-allowed',
              opacity: defaultEmail ? 1 : 0.5,
              fontSize: 13,
            }}
          >
            <input
              type="radio"
              name="evt-ts-mode"
              checked={mode === 'me'}
              onChange={() => setMode('me')}
              disabled={!defaultEmail}
            />
            <span>
              Send to me{' '}
              <code style={{ color: '#555', fontSize: 12 }}>
                ({defaultEmail || 'no internal email on session'})
              </code>
            </span>
          </label>

          <label
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              cursor: 'pointer', fontSize: 13,
            }}
          >
            <input
              type="radio"
              name="evt-ts-mode"
              checked={mode === 'sender'}
              onChange={() => setMode('sender')}
              disabled={internalSenders.length === 0}
            />
            <span>Send to a registered sender:</span>
            <select
              className={styles.input}
              value={pickedSenderEmail}
              onChange={(e) => setPickedSenderEmail(e.target.value)}
              disabled={mode !== 'sender'}
              style={{ flex: 1 }}
            >
              {internalSenders.map((s) => (
                <option key={s.email} value={s.email}>
                  {s.email}
                </option>
              ))}
            </select>
          </label>

          <label
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              cursor: 'pointer', fontSize: 13,
            }}
          >
            <input
              type="radio"
              name="evt-ts-mode"
              checked={mode === 'custom'}
              onChange={() => setMode('custom')}
            />
            <span>Other internal address:</span>
            <input
              className={styles.input}
              type="email"
              value={customEmail}
              onChange={(e) => setCustomEmail(e.target.value)}
              placeholder={`name${INTERNAL_DOMAIN_SUFFIX}`}
              disabled={mode !== 'custom'}
              style={{ flex: 1 }}
            />
          </label>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
          <button type="button" className={styles.button} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={`${styles.button} ${styles.buttonPrimary}`}
            onClick={handleSend}
            disabled={sendMut.isPending || !eventConfigured}
            title={
              !eventConfigured
                ? 'Set Event Details first'
                : 'Send a real calendar invite for preview'
            }
          >
            {sendMut.isPending ? 'Sending…' : 'Send test invite'}
          </button>
        </div>
      </div>
    </div>
  );
}
